import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withSelfStorageSchema } from 'decorators/search';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
// Bare SCSS import ensures StarRating styles load before FacilityCard styles to
// preserve MiniCssExtractPlugin ordering (avoids specificity conflicts).
import 'components/review/StarRating/StarRating.scss';
import { TooltipBanner } from 'components/core/TooltipBanner';
import ExpressIcon from 'components/svg/ExpressIcon';
import { FlexColumn, FlexRow } from 'components/atoms/Flex';

import { FacilityImage } from 'components/facility/FacilityImage';

import { NewStackedUnits } from 'components/molecules/StackedUnits';
import { Badge } from 'components/molecules/Badge';
import { LoadingFacilityCard } from 'components/facility/FacilityCard/LoadingFacilityCard';
import { DealsBanner, getDealText } from 'components/facility/DealsBanner';

import './FacilityCard.scss';
import Marker from 'components/svg/Marker';
import { trackCTAClick } from 'components/core/library/tracking-utils/tracking/clickTrackingUtils';
import { ViewportAction } from '@sparefoot/react-core';
import StarRating from '../review/StarRating/StarRating';
import withOnVisibleFacilityHandler from 'decorators/facility/withOnVisibleFacilityHandler';

export function FacilityCard({
	facility,
	baseUrl = '',
	facilitySelected,
	handleFacilitySelected,
	handleFacilityDeselected,
	handlePinDeselected,
	lazyLoad,
	storageType = 'storage',
	searchParams,
	selfStorageSchema,
	withSchema,
	hasOMI,
	miniCard,
	onVisible,
	inMapView,
	isDealsPage,
}) {
	const handleDeselect = useCallback(() => {
		handleFacilityDeselected();
		handlePinDeselected();
	}, [handleFacilityDeselected, handlePinDeselected]);

	const handleSelect = useCallback(() => {
		if (!miniCard) {
			handleFacilitySelected(facility.id);
			handlePinDeselected();
		}
	}, [miniCard, handleFacilitySelected, handlePinDeselected, facility]);

	const handleTrackCTA = useCallback(() => {
		trackCTAClick(
			'view-all-units',
			'View all units/prices Button',
			'Compare all units at this facility',
			facility.url,
		);
	}, [facility]);

	const dealText = getDealText(facility.units, isDealsPage);

	if (facility.loading) {
		return <LoadingFacilityCard />;
	}

	return (
		<ViewportAction
			onVisible={onVisible}
			className="facility-card-wrapper"
		>
			<FlexColumn
				className={classnames(
					'new-clp-facility-card',
					'facility-card-loaded',
					{ selected: facility.id === facilitySelected },
					{ miniCard },
					{ 'in-map-view': inMapView },
				)}
				onMouseEnter={handleSelect}
				onMouseLeave={inMapView ? undefined : handleDeselect}
			>
				<FlexColumn
					gapSmall
					className="facility-head"
				>
					<Link
						href={`${baseUrl}${facility.url}`}
						productClick
					>
						{hasOMI && (
							<TooltipBanner
								className="facility-card-omi-banner"
								tooltipContent={
									'Move into this facility online, eliminating the need to fill out rental paperwork at the facility when you plan to move in.'
								}
								bannerText="Online Move-In Available"
								textColor="darkGray"
								bannerIcon={<ExpressIcon />}
								theme="green"
							/>
						)}
						{dealText && <DealsBanner text={dealText} />}
						<FacilityImage
							src={facility.images[0]}
							alt={`Photo of ${facility.name}`}
							size="medium"
							lazyLoad={lazyLoad}
						/>
					</Link>
				</FlexColumn>
				<FlexColumn
					className="facility-details"
					gapSmall
				>
					<FlexRow
						justifyBetween
						alignCenter
						gapSmall
					>
						<Badge
							badgeColor="blue"
							className="distance-badge"
							badgeIcon={<Marker fill="#146fb4" />}
							badgeText={
								<Paragraph
									size="tiny"
									color="priceBlue"
									weight="standard"
									last
								>
									<Text
										size="tiny"
										color="priceBlue"
										weight="extraBold"
									>
										{`${facility.distance}`}
									</Text>{' '}
									miles away
								</Paragraph>
							}
						/>
						{!!facility.reviews.number && (
							<Badge
								className="reviews-badge"
								badgeText={
									<FlexRow
										alignCenter
										gapTiny
									>
										<StarRating
											width={9}
											height={9}
											rating={facility.reviews.average}
											fillColor="#146FB4"
											outOf={5}
										/>

										<Text
											size="tiny"
											color="priceBlue"
											weight="extraBold"
											last
										>
											{facility.reviews.average.toFixed(
												1,
											)}
										</Text>
										<Text
											size="tiny"
											color="blue"
											last
										>
											({facility.reviews.number})
										</Text>
									</FlexRow>
								}
								badgeColor="blue"
							></Badge>
						)}
					</FlexRow>
					<Link
						href={`${baseUrl}${facility.url}`}
						productClick
					>
						<FlexColumn gapSmall>
							<H3
								className="facility-name"
								color="black"
								last
							>
								{facility.name}
							</H3>
							<Paragraph
								className="facility-location"
								last
								color="gray"
							>
								{`${facility.location?.streetAddress1}, ${facility.location?.city}, ${facility.location?.state}, ${facility.location?.zip}`}
							</Paragraph>
						</FlexColumn>
					</Link>
					<NewStackedUnits
						facility={facility}
						baseUrl={baseUrl}
						units={facility.units}
						storageType={storageType}
						searchParams={searchParams}
						withScarcity
					/>
					<Button
						className="view-all-units"
						href={`${baseUrl}${facility.url}`}
						secondary
						centered
						onClick={handleTrackCTA}
					>
						View All Units
					</Button>
				</FlexColumn>
				{withSchema && (
					<script
						key="schema"
						type="application/ld+json"
						dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
					/>
				)}
			</FlexColumn>
		</ViewportAction>
	);
}

FacilityCard.propTypes = {
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleFacilitySelected: PropTypes.func,
	handleFacilityDeselected: PropTypes.func,
	handlePinDeselected: PropTypes.func,
	lazyLoad: PropTypes.bool,
	searchParams: PropTypes.object,
	selfStorageSchema: PropTypes.string,
	storageType: PropTypes.oneOf(['storage', 'vehicle']),
	withSchema: PropTypes.bool,
	hasOMI: PropTypes.bool,
	miniCard: PropTypes.bool,
	onVisible: PropTypes.func,
	inMapView: PropTypes.bool,
};

export default withOnVisibleFacilityHandler(
	withSelfStorageSchema(FacilityCard),
);
